import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { geocodingService, LocationSuggestion } from '../../services/geocoding.service';

interface LocationAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string, location?: LocationSuggestion) => void;
  onLocationSelect?: (location: LocationSuggestion) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
}

export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  label,
  value,
  onChange,
  onLocationSelect,
  error,
  required,
  placeholder = 'Search for a city...',
}) => {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationSuggestion | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = async (inputValue: string) => {
    onChange(inputValue);
    setSelectedLocation(null);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (inputValue.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    setShowDropdown(true);

    debounceRef.current = setTimeout(async () => {
      const results = await geocodingService.searchCities(inputValue);
      setSuggestions(results);
      setIsLoading(false);
    }, 500);
  };

  const handleSelectSuggestion = (suggestion: LocationSuggestion) => {
    const cityName = suggestion.city;
    onChange(cityName, suggestion);
    setSelectedLocation(suggestion);
    setSuggestions([]);
    setShowDropdown(false);
    
    if (onLocationSelect) {
      onLocationSelect(suggestion);
    }
  };

  return (
    <div className="w-full" ref={wrapperRef}>
      {label && (
        <label className="block text-sm font-medium text-neutral-charcoal mb-1">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className="relative">
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowDropdown(true);
              }
            }}
            placeholder={placeholder}
            className={`w-full pl-10 pr-3 py-2 bg-white border rounded-lg shadow-sm transition-colors placeholder:text-gray-400
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
            disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-500 ${
              error ? 'border-error focus-visible:ring-error' : 'border-gray-300 focus-visible:ring-primary'
            }`}
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            {isLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <MapPin size={18} />
            )}
          </div>
          {selectedLocation && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✓
              </span>
            </div>
          )}
        </div>

        {showDropdown && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full px-4 py-3 text-left hover:bg-neutral-light transition-colors border-b border-gray-100 last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-charcoal truncate">
                      {suggestion.city}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {suggestion.country}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {showDropdown && !isLoading && suggestions.length === 0 && value.length >= 2 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center">
            <p className="text-sm text-gray-500">No cities found</p>
          </div>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-error">{error}</p>}
    </div>
  );
};
